import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  Merge,
  AlertTriangle,
  CheckCircle,
  Building2,
  Phone,
  Mail,
  MapPin,
  Calendar,
  User,
  ChevronDown,
  ChevronRight,
  Search,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useCompanies } from '@/hooks/useCompanies';
import { useQueryClient } from '@tanstack/react-query';
import { companyKeys } from '@/hooks/useCompanies';
import { getProjectsByReferralSource } from '@/services/projects';
import { calculateVisitStatus, formatDaysSinceVisit } from '@/services/visits';
import { findDuplicateCompanies, mergeCompanies } from '@/services/mergeCompanies';
import type { DuplicateGroup, MergeSummary } from '@/services/mergeCompanies';
import type { Company } from '@/types';

// ===========================================
// Main Page Component
// ===========================================

type Step = 'detect' | 'compare' | 'merging' | 'result';

export function MergeCompaniesPage() {
  const { data: companies, isLoading, refetch } = useCompanies();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('detect');
  const [selectedGroup, setSelectedGroup] = useState<DuplicateGroup | null>(null);
  const [keepId, setKeepId] = useState<string>('');
  const [merging, setMerging] = useState(false);
  const [lastResult, setLastResult] = useState<{ summary: MergeSummary; keptName: string; retiredName: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSimilar, setShowSimilar] = useState(true);

  // Detect duplicates
  const duplicateGroups = useMemo(() => {
    if (!companies) return [];
    return findDuplicateCompanies(companies);
  }, [companies]);

  // Filter groups by search
  const filteredGroups = useMemo(() => {
    let groups = duplicateGroups;
    if (!showSimilar) {
      groups = groups.filter((g) => g.matchType === 'exact');
    }
    if (!searchQuery) return groups;
    const q = searchQuery.toLowerCase();
    return groups.filter((g) =>
      g.companies.some((c) => c.name.toLowerCase().includes(q))
    );
  }, [duplicateGroups, searchQuery, showSimilar]);

  const exactCount = duplicateGroups.filter((g) => g.matchType === 'exact').length;
  const similarCount = duplicateGroups.filter((g) => g.matchType === 'similar').length;

  const handleSelectGroup = (group: DuplicateGroup) => {
    setSelectedGroup(group);
    setKeepId(group.companies[0].id);
    setStep('compare');
  };

  const handleMerge = async () => {
    if (!selectedGroup || !keepId) return;
    const retireCompany = selectedGroup.companies.find((c) => c.id !== keepId);
    if (!retireCompany) return;

    const keepCompany = selectedGroup.companies.find((c) => c.id === keepId)!;

    if (!confirm(
      `Are you sure you want to merge "${retireCompany.name}" into "${keepCompany.name}"?\n\n` +
      `All projects, visits, tokens, and notes will be moved to "${keepCompany.name}".\n` +
      `"${retireCompany.name}" will be permanently deleted.\n\n` +
      `This cannot be undone.`
    )) {
      return;
    }

    setStep('merging');
    setMerging(true);

    try {
      const result = await mergeCompanies(keepId, retireCompany.id);
      setLastResult({
        summary: result.summary,
        keptName: result.keptCompanyName,
        retiredName: result.retiredCompanyName,
      });
      setStep('result');
      // Invalidate companies cache
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
      toast.success('Companies merged successfully');
    } catch (error: any) {
      console.error('Merge failed:', error);
      toast.error(error?.message || 'Failed to merge companies');
      setStep('compare');
    } finally {
      setMerging(false);
    }
  };

  const handleBackToDetect = () => {
    setStep('detect');
    setSelectedGroup(null);
    setKeepId('');
    setLastResult(null);
    refetch();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin" className="text-gray-500 hover:text-gray-700">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Merge Duplicate Companies</h1>
          <p className="text-gray-600 mt-1">
            Find and combine duplicate referral source entries.
          </p>
        </div>
      </div>

      {/* Step content */}
      {step === 'detect' && (
        <DetectStep
          groups={filteredGroups}
          exactCount={exactCount}
          similarCount={similarCount}
          loading={isLoading}
          searchQuery={searchQuery}
          showSimilar={showSimilar}
          onSearchChange={setSearchQuery}
          onToggleSimilar={() => setShowSimilar(!showSimilar)}
          onSelectGroup={handleSelectGroup}
          onRefresh={() => refetch()}
        />
      )}

      {step === 'compare' && selectedGroup && (
        <CompareStep
          group={selectedGroup}
          keepId={keepId}
          onKeepChange={setKeepId}
          onMerge={handleMerge}
          onBack={() => setStep('detect')}
        />
      )}

      {step === 'merging' && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <RefreshCw className="animate-spin text-blue-600 mx-auto mb-4" size={48} />
          <p className="text-lg font-semibold text-gray-900">Merging companies...</p>
          <p className="text-gray-600 mt-2">
            Updating projects, visits, tokens, notes, and Portal records.
          </p>
        </div>
      )}

      {step === 'result' && lastResult && (
        <ResultStep
          result={lastResult}
          onBack={handleBackToDetect}
        />
      )}
    </div>
  );
}

// ===========================================
// Step 1: Detect Duplicates
// ===========================================

function DetectStep({
  groups,
  exactCount,
  similarCount,
  loading,
  searchQuery,
  showSimilar,
  onSearchChange,
  onToggleSimilar,
  onSelectGroup,
  onRefresh,
}: {
  groups: DuplicateGroup[];
  exactCount: number;
  similarCount: number;
  loading: boolean;
  searchQuery: string;
  showSimilar: boolean;
  onSearchChange: (q: string) => void;
  onToggleSimilar: () => void;
  onSelectGroup: (g: DuplicateGroup) => void;
  onRefresh: () => void;
}) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center">
        <RefreshCw className="animate-spin text-blue-600 mx-auto mb-4" size={48} />
        <p className="text-gray-600">Scanning for duplicates...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-red-600">{exactCount}</span>
          <span className="text-sm text-gray-600">exact duplicates</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-amber-600">{similarCount}</span>
          <span className="text-sm text-gray-600">similar names</span>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showSimilar}
              onChange={onToggleSimilar}
              className="rounded border-gray-300"
            />
            Show similar names
          </label>
          <button onClick={onRefresh} className="btn btn-secondary text-sm">
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search by company name..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="input pl-10"
        />
      </div>

      {/* Groups list */}
      {groups.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <CheckCircle size={48} className="mx-auto mb-3 text-green-500" />
          <p className="text-lg font-semibold text-gray-900">No duplicates found</p>
          <p className="text-gray-600 mt-1">All company names are unique.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {groups.map((group) => (
            <DuplicateGroupCard
              key={group.key}
              group={group}
              expanded={expandedKey === group.key}
              onToggle={() => setExpandedKey(expandedKey === group.key ? null : group.key)}
              onSelect={() => onSelectGroup(group)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ===========================================
// Duplicate Group Card
// ===========================================

function DuplicateGroupCard({
  group,
  expanded,
  onToggle,
  onSelect,
}: {
  group: DuplicateGroup;
  expanded: boolean;
  onToggle: () => void;
  onSelect: () => void;
}) {
  return (
    <div className="bg-white rounded-lg shadow">
      <button
        onClick={onToggle}
        className="w-full text-left p-4 flex items-center gap-3 hover:bg-gray-50"
      >
        {expanded ? <ChevronDown size={20} className="text-gray-400" /> : <ChevronRight size={20} className="text-gray-400" />}
        <Building2 size={20} className="text-blue-600" />
        <span className="font-semibold text-gray-900 flex-1">{group.displayName}</span>
        <span className={`text-xs font-medium px-2 py-1 rounded ${
          group.matchType === 'exact'
            ? 'bg-red-100 text-red-700'
            : 'bg-amber-100 text-amber-700'
        }`}>
          {group.matchType === 'exact' ? 'Exact Match' : 'Similar Name'}
        </span>
        <span className="text-sm text-gray-500">{group.companies.length} entries</span>
      </button>

      {expanded && (
        <div className="border-t px-4 pb-4">
          <div className="space-y-2 mt-3">
            {group.companies.map((company) => (
              <CompanyRow key={company.id} company={company} />
            ))}
          </div>
          <button
            onClick={onSelect}
            className="mt-4 btn btn-primary w-full"
          >
            <Merge size={16} />
            Compare & Merge
          </button>
        </div>
      )}
    </div>
  );
}

// ===========================================
// Company Row (compact display)
// ===========================================

function CompanyRow({ company }: { company: Company }) {
  const { status } = calculateVisitStatus(company.lastVisitDate || null);
  const dotColor = status === 'green' ? 'bg-green-500' : status === 'yellow' ? 'bg-yellow-500' : 'bg-red-500';

  return (
    <div className="flex items-center gap-3 p-2 bg-gray-50 rounded text-sm">
      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotColor}`} />
      <span className="font-medium text-gray-900 min-w-0 truncate">{company.name}</span>
      {company.type && (
        <span className="badge badge-blue text-xs flex-shrink-0">{company.type}</span>
      )}
      {company.phone && (
        <span className="flex items-center gap-1 text-gray-500 flex-shrink-0">
          <Phone size={12} />
          {company.phone}
        </span>
      )}
      {company.email && (
        <span className="flex items-center gap-1 text-gray-500 flex-shrink-0 truncate">
          <Mail size={12} />
          {company.email}
        </span>
      )}
      {company.salesRepName && (
        <span className="flex items-center gap-1 text-gray-500 flex-shrink-0">
          <User size={12} />
          {company.salesRepName}
        </span>
      )}
    </div>
  );
}

// ===========================================
// Step 2: Compare & Select
// ===========================================

function CompareStep({
  group,
  keepId,
  onKeepChange,
  onMerge,
  onBack,
}: {
  group: DuplicateGroup;
  keepId: string;
  onKeepChange: (id: string) => void;
  onMerge: () => void;
  onBack: () => void;
}) {
  const [projectCounts, setProjectCounts] = useState<Record<string, number>>({});
  const [loadingCounts, setLoadingCounts] = useState(true);

  // Fetch project counts for each company
  React.useEffect(() => {
    async function fetchCounts() {
      setLoadingCounts(true);
      const counts: Record<string, number> = {};
      for (const company of group.companies) {
        try {
          const projects = await getProjectsByReferralSource(company.id);
          counts[company.id] = projects.length;
        } catch {
          counts[company.id] = 0;
        }
      }
      setProjectCounts(counts);
      setLoadingCounts(false);
    }
    fetchCounts();
  }, [group]);

  const retireCompany = group.companies.find((c) => c.id !== keepId);

  return (
    <div className="space-y-4">
      <button onClick={onBack} className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm">
        <ArrowLeft size={16} />
        Back to duplicates
      </button>

      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          Select which company to keep
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          All data from the retired company will be merged into the one you keep.
        </p>

        {/* Side-by-side comparison */}
        <div className={`grid gap-4 ${group.companies.length === 2 ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
          {group.companies.map((company) => (
            <CompanyCompareCard
              key={company.id}
              company={company}
              isKeep={company.id === keepId}
              projectCount={projectCounts[company.id] ?? null}
              loadingCount={loadingCounts}
              onSelect={() => onKeepChange(company.id)}
            />
          ))}
        </div>
      </div>

      {/* Merge action */}
      {retireCompany && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-semibold text-amber-800">Merge Summary</p>
              <p className="text-sm text-amber-700 mt-1">
                <strong>Keep:</strong> {group.companies.find((c) => c.id === keepId)?.name}<br />
                <strong>Retire:</strong> {retireCompany.name}<br />
                All projects, visits, partner tokens, notes, and outbound referrals from
                "{retireCompany.name}" will be moved to the kept company.
                The retired company will be permanently deleted.
              </p>
            </div>
          </div>
          <button
            onClick={onMerge}
            className="mt-4 btn bg-red-600 text-white hover:bg-red-700 w-full"
          >
            <Merge size={16} />
            Merge Companies
          </button>
        </div>
      )}
    </div>
  );
}

// ===========================================
// Company Comparison Card
// ===========================================

function CompanyCompareCard({
  company,
  isKeep,
  projectCount,
  loadingCount,
  onSelect,
}: {
  company: Company;
  isKeep: boolean;
  projectCount: number | null;
  loadingCount: boolean;
  onSelect: () => void;
}) {
  const { status, daysSince } = calculateVisitStatus(company.lastVisitDate || null);

  return (
    <button
      onClick={onSelect}
      className={`w-full text-left rounded-lg border-2 p-4 transition-all ${
        isKeep
          ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
          : 'border-gray-200 bg-white hover:border-gray-300'
      }`}
    >
      {/* Keep badge */}
      <div className="flex items-center justify-between mb-3">
        <span className={`text-xs font-bold px-2 py-1 rounded ${
          isKeep ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
        }`}>
          {isKeep ? 'KEEP' : 'RETIRE'}
        </span>
        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
          isKeep ? 'border-green-500' : 'border-gray-300'
        }`}>
          {isKeep && <div className="w-2 h-2 rounded-full bg-green-500" />}
        </div>
      </div>

      {/* Company info */}
      <h3 className="font-semibold text-gray-900 text-lg">{company.name}</h3>
      {company.type && (
        <span className="badge badge-blue text-xs mt-1 inline-block">{company.type}</span>
      )}

      <div className="mt-3 space-y-1.5 text-sm">
        {company.phone && (
          <div className="flex items-center gap-2 text-gray-600">
            <Phone size={14} /> {company.phone}
          </div>
        )}
        {company.email && (
          <div className="flex items-center gap-2 text-gray-600">
            <Mail size={14} /> {company.email}
          </div>
        )}
        {(company.city || company.state) && (
          <div className="flex items-center gap-2 text-gray-600">
            <MapPin size={14} /> {[company.city, company.state].filter(Boolean).join(', ')}
          </div>
        )}
        {company.salesRepName && (
          <div className="flex items-center gap-2 text-gray-600">
            <User size={14} /> {company.salesRepName}
          </div>
        )}
        <div className="flex items-center gap-2 text-gray-600">
          <Calendar size={14} /> {formatDaysSinceVisit(daysSince)}
        </div>
      </div>

      {/* Stats */}
      <div className="mt-3 pt-3 border-t flex items-center gap-4 text-xs text-gray-500">
        <span>
          {loadingCount ? '...' : `${projectCount ?? 0} projects`}
        </span>
        {company.commissionType && (
          <span className="text-green-600 font-medium">Has commission</span>
        )}
        {company.businessVisibility && (
          <span>Visibility: {company.businessVisibility}</span>
        )}
      </div>
    </button>
  );
}

// ===========================================
// Step 3: Result
// ===========================================

function ResultStep({
  result,
  onBack,
}: {
  result: { summary: MergeSummary; keptName: string; retiredName: string };
  onBack: () => void;
}) {
  const { summary, keptName, retiredName } = result;
  const total = Object.values(summary).reduce((a, b) => a + b, 0);

  return (
    <div className="bg-white rounded-lg shadow p-6 text-center">
      <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
      <h2 className="text-xl font-bold text-gray-900">Merge Complete</h2>
      <p className="text-gray-600 mt-2">
        <strong>"{retiredName}"</strong> has been merged into <strong>"{keptName}"</strong>.
      </p>

      {total > 0 && (
        <div className="mt-6 max-w-md mx-auto text-left">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Records moved:</h3>
          <div className="bg-gray-50 rounded-lg p-4 space-y-1 text-sm">
            {summary.contacts > 0 && <SummaryRow label="Contacts" count={summary.contacts} />}
            {summary.projects > 0 && <SummaryRow label="CRM Projects" count={summary.projects} />}
            {summary.portalProjects > 0 && <SummaryRow label="Portal Projects" count={summary.portalProjects} />}
            {summary.visits > 0 && <SummaryRow label="Visit Records" count={summary.visits} />}
            {summary.outboundReferrals > 0 && <SummaryRow label="Outbound Referrals" count={summary.outboundReferrals} />}
            {summary.partnerTokens > 0 && <SummaryRow label="Partner Tokens" count={summary.partnerTokens} />}
            {summary.partnerSessions > 0 && <SummaryRow label="Partner Sessions" count={summary.partnerSessions} />}
            {summary.evidence > 0 && <SummaryRow label="Evidence Records" count={summary.evidence} />}
            {summary.partnerNotes > 0 && <SummaryRow label="Partner Notes" count={summary.partnerNotes} />}
            {total === 0 && (
              <p className="text-gray-500 text-center">No records needed moving.</p>
            )}
          </div>
        </div>
      )}

      <button
        onClick={onBack}
        className="mt-6 btn btn-primary"
      >
        Back to Duplicates
      </button>
    </div>
  );
}

function SummaryRow({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex justify-between">
      <span className="text-gray-600">{label}</span>
      <span className="font-semibold text-gray-900">{count}</span>
    </div>
  );
}
